"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import BrainDesktopDrop from "./BrainDesktopDrop";
import BrainMobileAbout from "./mobile/BrainMobileAbout";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./my-stream/MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./my-stream/MyStreamWaveOutcome";
import { WaveWinners } from "../waves/winners/WaveWinners";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import MyStreamWaveMyVotes from "./my-stream/votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./my-stream/MyStreamWaveFAQ";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import BrainMobileWaves from "./mobile/BrainMobileWaves";
import BrainMobileMessages from "./mobile/BrainMobileMessages";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import BrainNotifications from "./notifications/NotificationsContainer";
import { getHomeFeedRoute } from "@/helpers/navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
export enum BrainView {
  DEFAULT = "DEFAULT",
  ABOUT = "ABOUT",
  LEADERBOARD = "LEADERBOARD",
  WINNERS = "WINNERS",
  OUTCOME = "OUTCOME",
  MY_VOTES = "MY_VOTES",
  FAQ = "FAQ",
  WAVES = "WAVES",
  MESSAGES = "MESSAGES",
  NOTIFICATIONS = "NOTIFICATIONS",
}
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(101);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const [hydrated, setHydrated] = useState(false);
  const myStream = useMyStreamOptional();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setHydrated(true);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [activeView, setActiveView] = useState(BrainView.DEFAULT);
  const dropId = searchParams?.get("drop") ?? undefined;
  const {
    data: drop
  } = useQuery({
    queryKey: [QueryKey.DROP, {
      drop_id: dropId
    }],
    queryFn: async () => await commonApiFetch({
      endpoint: `drops/${dropId}`
    }),
    placeholderData: keepPreviousData,
    enabled: !!dropId
  });
  let t3;
  if ($[2] !== myStream?.activeWave.id || $[3] !== searchParams) {
    t3 = myStream?.activeWave.id ?? searchParams?.get("wave") ?? null;
    $[2] = myStream?.activeWave.id;
    $[3] = searchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const waveId = t3;
  let t4;
  if ($[5] !== pathname || $[6] !== router || $[7] !== searchParams) {
    t4 = () => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.delete("wave");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeFeedRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[5] = pathname;
    $[6] = router;
    $[7] = searchParams;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4 || $[10] !== waveId) {
    t5 = {
      waveId,
      onWaveNotFound: t4
    };
    $[9] = t4;
    $[10] = waveId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: wave
  } = useWaveData(t5);
  const {
    isMemesWave
  } = useWave(wave);
  const {
    voting: t6,
    decisions: t7
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t6;
  const {
    firstDecisionDone
  } = t7;
  let t8;
  if ($[12] !== pathname || $[13] !== router || $[14] !== searchParams) {
    t8 = drop_0 => {
      const params_0 = new URLSearchParams(searchParams?.toString() || "");
      params_0.set("drop", drop_0.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[12] = pathname;
    $[13] = router;
    $[14] = searchParams;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const onDropClick = t8;
  let t9;
  if ($[16] !== pathname || $[17] !== router || $[18] !== searchParams) {
    t9 = () => {
      const params_1 = new URLSearchParams(searchParams?.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeFeedRoute();
      router.push(newUrl_0, {
        scroll: false
      });
    };
    $[16] = pathname;
    $[17] = router;
    $[18] = searchParams;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const onDropClose = t9;
  const isDropOpen = drop && drop?.id?.toLowerCase() === dropId?.toLowerCase();
  const isRankWave = wave?.wave.type === ApiWaveType.Rank;
  const hasWave = Boolean(waveId);
  let t10;
  let t11;
  if ($[20] !== isApp || $[21] !== pathname || $[22] !== searchParams || $[23] !== waveId) {
    t10 = () => {
      const tabParam = searchParams?.get("tab");
      const viewParam = searchParams?.get("view");
      const createParam = searchParams?.get("create");
      if (createParam && isApp) {
        setActiveView(BrainView.DEFAULT);
        return;
      }
      if (!waveId && pathname === "/notifications" || !waveId && viewParam === "notifications") {
        setActiveView(BrainView.NOTIFICATIONS);
        return;
      }
      if (!waveId && pathname === "/messages" || !waveId && viewParam === "messages") {
        setActiveView(BrainView.MESSAGES);
        return;
      }
      if (!waveId && pathname === "/waves" || !waveId && viewParam === "waves") {
        setActiveView(BrainView.WAVES);
        return;
      }
      if (pathname === "/" && (!tabParam || tabParam === "feed") && !waveId && !viewParam) {
        setActiveView(BrainView.DEFAULT);
      }
    };
    t11 = [pathname, searchParams, waveId, isApp];
    $[20] = isApp;
    $[21] = pathname;
    $[22] = searchParams;
    $[23] = waveId;
    $[24] = t10;
    $[25] = t11;
  } else {
    t10 = $[24];
    t11 = $[25];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[26] !== activeView || $[27] !== firstDecisionDone || $[28] !== hasWave || $[29] !== isCompleted || $[30] !== isMemesWave || $[31] !== pathname || $[32] !== wave || $[33] !== waveId) {
    t12 = () => {
      const globalViews = new Set([BrainView.DEFAULT, BrainView.WAVES, BrainView.MESSAGES, BrainView.NOTIFICATIONS]);
      const routeToView = {
        "/waves": BrainView.WAVES,
        "/messages": BrainView.MESSAGES,
        "/notifications": BrainView.NOTIFICATIONS
      };
      if (!hasWave) {
        const isWaveSpecificView = !globalViews.has(activeView);
        if (isWaveSpecificView) {
          setActiveView(routeToView[pathname ?? ""] ?? BrainView.DEFAULT);
        }
        return;
      }
      if (!wave) {
        return;
      }
      const shouldResetToDefault = activeView === BrainView.LEADERBOARD && isCompleted || activeView === BrainView.WINNERS && !firstDecisionDone || activeView === BrainView.MY_VOTES && !isMemesWave || activeView === BrainView.FAQ && !isMemesWave;
      if (shouldResetToDefault) {
        setActiveView(BrainView.DEFAULT);
        return;
      }
      const nonWaveViews = new Set([BrainView.NOTIFICATIONS, BrainView.MESSAGES, BrainView.WAVES]);
      if (waveId && nonWaveViews.has(activeView)) {
        setActiveView(BrainView.DEFAULT);
      }
    };
    t13 = [hasWave, wave, isCompleted, firstDecisionDone, activeView, isMemesWave, waveId, pathname];
    $[26] = activeView;
    $[27] = firstDecisionDone;
    $[28] = hasWave;
    $[29] = isCompleted;
    $[30] = isMemesWave;
    $[31] = pathname;
    $[32] = wave;
    $[33] = waveId;
    $[34] = t12;
    $[35] = t13;
  } else {
    t12 = $[34];
    t13 = $[35];
  }
  useEffect(t12, t13);
  let t14;
  if ($[36] !== pathname || $[37] !== router || $[38] !== searchParams) {
    t14 = () => {
      const params_2 = new URLSearchParams(searchParams?.toString() || "");
      params_2.delete("create");
      const base = pathname ?? "/";
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[36] = pathname;
    $[37] = router;
    $[38] = searchParams;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const closeCreateOverlay = t14;
  let t15;
  bb0: {
    if (!isApp) {
      t15 = null;
      break bb0;
    }
    const createParam_0 = searchParams?.get("create");
    if (!createParam_0) {
      t15 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t15 = null;
      break bb0;
    }
    if (createParam_0 === "dm") {
      let t16;
      if ($[40] !== closeCreateOverlay || $[41] !== connectedProfile) {
        t16 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[40] = closeCreateOverlay;
        $[41] = connectedProfile;
        $[42] = t16;
      } else {
        t16 = $[42];
      }
      t15 = t16;
      break bb0;
    }
    if (createParam_0 === "wave") {
      let t16;
      if ($[43] !== closeCreateOverlay || $[44] !== connectedProfile) {
        t16 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[43] = closeCreateOverlay;
        $[44] = connectedProfile;
        $[45] = t16;
      } else {
        t16 = $[45];
      }
      t15 = t16;
      break bb0;
    }
    t15 = null;
  }
  const createOverlay = t15;
  let t16;
  if ($[46] !== waveId) {
    t16 = <BrainMobileAbout activeWaveId={waveId} />;
    $[46] = waveId;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== isRankWave || $[49] !== onDropClick || $[50] !== wave) {
    t17 = isRankWave && !!wave ? <MyStreamWaveLeaderboard wave={wave} onDropClick={onDropClick} /> : null;
    $[48] = isRankWave;
    $[49] = onDropClick;
    $[50] = wave;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== isRankWave || $[53] !== onDropClick || $[54] !== wave) {
    t18 = isRankWave && !!wave ? <div className="tw-px-2 sm:tw-px-4"><WaveWinners wave={wave} onDropClick={onDropClick} /></div> : null;
    $[52] = isRankWave;
    $[53] = onDropClick;
    $[54] = wave;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== isRankWave || $[57] !== wave) {
    t19 = isRankWave && !!wave ? <MyStreamWaveOutcome wave={wave} /> : null;
    $[56] = isRankWave;
    $[57] = wave;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== isRankWave || $[60] !== onDropClick || $[61] !== wave) {
    t20 = isRankWave && !!wave ? <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} /> : null;
    $[59] = isRankWave;
    $[60] = onDropClick;
    $[61] = wave;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== isMemesWave || $[64] !== isRankWave || $[65] !== wave) {
    t21 = isRankWave && isMemesWave && !!wave ? <MyStreamWaveFAQ wave={wave} /> : null;
    $[63] = isMemesWave;
    $[64] = isRankWave;
    $[65] = wave;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <BrainMobileWaves />;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <BrainMobileMessages />;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <BrainNotifications />;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== children || $[71] !== t16 || $[72] !== t17 || $[73] !== t18 || $[74] !== t19 || $[75] !== t20 || $[76] !== t21) {
    t25 = {
      [BrainView.ABOUT]: t16,
      [BrainView.DEFAULT]: children,
      [BrainView.LEADERBOARD]: t17,
      [BrainView.WINNERS]: t18,
      [BrainView.OUTCOME]: t19,
      [BrainView.MY_VOTES]: t20,
      [BrainView.FAQ]: t21,
      [BrainView.WAVES]: t22,
      [BrainView.MESSAGES]: t23,
      [BrainView.NOTIFICATIONS]: t24
    };
    $[70] = children;
    $[71] = t16;
    $[72] = t17;
    $[73] = t18;
    $[74] = t19;
    $[75] = t20;
    $[76] = t21;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  const viewComponents = t25;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t26;
  if ($[78] !== drop || $[79] !== dropOverlayClass || $[80] !== isDropOpen || $[81] !== onDropClose) {
    t26 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[78] = drop;
    $[79] = dropOverlayClass;
    $[80] = isDropOpen;
    $[81] = onDropClose;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== activeView || $[84] !== hasWave || $[85] !== hydrated || $[86] !== isApp || $[87] !== wave) {
    t27 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={setActiveView} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[83] = activeView;
    $[84] = hasWave;
    $[85] = hydrated;
    $[86] = isApp;
    $[87] = wave;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  let t29;
  let t30;
  let t31;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      opacity: 0,
      x: 20
    };
    t29 = {
      opacity: 1,
      x: 0
    };
    t30 = {
      opacity: 0,
      x: -20
    };
    t31 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[89] = t28;
    $[90] = t29;
    $[91] = t30;
    $[92] = t31;
  } else {
    t28 = $[89];
    t29 = $[90];
    t30 = $[91];
    t31 = $[92];
  }
  const t32 = viewComponents[activeView];
  let t33;
  if ($[93] !== activeView || $[94] !== t32) {
    t33 = <AnimatePresence mode="wait"><motion.div key={activeView} initial={t28} animate={t29} exit={t30} transition={t31} className="tw-flex-1">{t32}</motion.div></AnimatePresence>;
    $[93] = activeView;
    $[94] = t32;
    $[95] = t33;
  } else {
    t33 = $[95];
  }
  let t34;
  if ($[96] !== createOverlay || $[97] !== t26 || $[98] !== t27 || $[99] !== t33) {
    t34 = <div className="tw-relative tw-flex tw-flex-col tw-h-full">{createOverlay}{t26}{t27}{t33}</div>;
    $[96] = createOverlay;
    $[97] = t26;
    $[98] = t27;
    $[99] = t33;
    $[100] = t34;
  } else {
    t34 = $[100];
  }
  return t34;
};
export default BrainMobile;